/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { useCallback } from 'react';

import { breakpoints, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import Carousel from 'bundles/design-system/components/Carousel';
import type { CarouselSettings } from 'bundles/design-system/components/types';
import type {
  EntWebsiteBlockTestimonialCarouselQuery,
  EntWebsiteTestimonialCarouselFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout, { noImage } from 'bundles/ent-website/contentful/core/BlockLayout';
import { TestimonialModule } from 'bundles/ent-website/contentful/modules/TestimonialModule';
import { entWebsiteBlockTestimonialCarouselQuery } from 'bundles/ent-website/queries/EntWebsiteTestimonialCarousel';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';

import 'css!bundles/design-system/components/__styles__/Carousel';

type Props = BlockModuleInnerProps<EntWebsiteTestimonialCarouselFragment>;

const carouselButtonColorMap: Record<string, string | undefined> = {
  'white-bg': 'var(--cds-color-white-0)',
  'grey-bg': 'var(--cds-color-grey-25)',
  'light-blue-bg': 'var(--ui-neutral-background-bg-light-bg-blue-100)',
};

const CAROUSEL_SETTINGS: CarouselSettings = {
  dots: true,
  arrows: true,
  infinite: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  responsive: [],
  // react-slick is super broken in RTL, so we have to use tricks to make it work. This means disabling `lazyLoad`,
  // since it assumes LTR and doesn't even render the things in the viewport, and doing tricks to fixup the position
  // when it loads (see tricks in static/bundles/design-system/components/Carousel.tsx).
  lazyLoad: undefined,
};

const DEFAULT_CARD_VISIBILITY = {
  authorAvatar: true,
  headline: true,
  testimonial: true,
  author: true,
  authorOrganization: true,
  authorTitle: true,
};

const styles = {
  container: (_theme: Theme, assemblyTheme: string) => css`
    .slick-slider {
      width: 100% !important;
    }

    .slider-button {
      background: ${carouselButtonColorMap[assemblyTheme] ?? 'var(--cds-color-white-0)'};
    }

    .slider-right {
      left: unset !important;
      right: calc(var(--cds-spacing-400) * -1) !important;
    }

    .slider-left {
      right: unset !important;
      left: calc(var(--cds-spacing-400) * -1) !important;
    }

    .BlockLayout_Container {
      padding-bottom: var(--cds-spacing-150);
      padding-top: 0;
    }

    ${breakpoints.down('xs')} {
      .slider-button {
        svg {
          width: 32px !important;
          height: 32px !important;
        }
      }

      .slider-right {
        right: calc(var(--cds-spacing-300) * -1) !important;
      }

      .slider-left {
        left: calc(var(--cds-spacing-300) * -1) !important;
      }
    }
  `,
};

export function TestimonialCarouselModule({ content, assembly }: Props) {
  const theme = useTheme();
  const { testimonialsCollection } = content;

  const contentPanel = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    () => (
      <div css={styles.container(theme, assembly?.theme ?? 'white-bg')}>
        <Carousel settings={CAROUSEL_SETTINGS}>
          {testimonialsCollection?.items?.map(
            (item) =>
              item && (
                <TestimonialModule
                  isCarouselSlide
                  key={item?.slug}
                  content={item}
                  assembly={{
                    ...assembly,
                    // Enable all visibility
                    visibility: {
                      ...DEFAULT_CARD_VISIBILITY,
                    },
                  }}
                />
              )
          )}
        </Carousel>
      </div>
    ),
    [assembly, testimonialsCollection?.items, theme]
  );

  return <BlockLayout assembly={noImage(assembly)} renderContent={contentPanel} />;
}

export const EntWebsiteTestimonialCarousel = withBlockQuery<
  EntWebsiteTestimonialCarouselFragment,
  // @ts-expect-error TSMIGRATION-5.0 TS2344
  EntWebsiteBlockTestimonialCarouselQuery
>(entWebsiteBlockTestimonialCarouselQuery)(TestimonialCarouselModule);
